import { useMemo } from 'react';
import { useT } from '@/i18n/LanguageContext';
import { PURPOSES, purposeColor, hexToRgba, contrastText } from './purposes';

// Visual /24 map: 256 cells (16×16). Cell index 0..255 = last octet.
// .0 = network, .255 = broadcast → inert. .1..254 map to live entries.
// mode: 'state' (free/assigned/auth-free) | 'purpose' (color by 用途 range).
const STATE_CLS = {
  available: 'bg-slate-50 border-slate-200 text-slate-400 hover:border-blue-400 hover:bg-blue-50 cursor-pointer hover:scale-110',
  assigned: 'bg-blue-100 border-blue-300 text-blue-900 hover:bg-blue-200 hover:border-blue-400 cursor-pointer hover:scale-110',
  auth_free: 'bg-emerald-100 border-emerald-300 text-emerald-900 hover:bg-emerald-200 hover:border-emerald-400 cursor-pointer hover:scale-110',
  reserved: 'bg-slate-200 border-slate-300 text-slate-500 cursor-default',
};

function stateOf(octet, e) {
  if (octet === 0 || octet === 255) return 'reserved';
  if (e?.auth_free) return 'auth_free';
  if (e?.status === 'assigned') return 'assigned';
  return 'available';
}

export default function IpMap({ entries, subnet, mode = 'state', onCellClick }) {
  const { t } = useT();
  const byOctet = useMemo(() => {
    const m = {};
    for (const e of entries || []) {
      const o = parseInt((e.ip_address || '').split('.').pop(), 10);
      if (!Number.isNaN(o)) m[o] = e;
    }
    return m;
  }, [entries]);

  const base = (subnet || '0.0.0.0/24').split('/')[0].split('.').slice(0, 3).join('.');

  const cells = [];
  for (let o = 0; o < 256; o += 1) {
    const e = byOctet[o];
    const st = stateOf(o, e);
    const reserved = st === 'reserved';
    const ip = `${base}.${o}`;
    const purpose = e?.purpose;
    // T3/T2 — ラベルは固定辞書、値（IP・MAC・氏名・端末名）はそのまま。
    // ツールチップ全体をモデルに訳させると別人の名前に化けるため data-no-translate。
    const title = reserved
      ? `${ip}（${o === 0 ? t('it.ip.network') : t('it.ip.broadcast')}）`
      : [ip,
         purpose && `${t('it.ip.purpose')}: ${purpose}`,
         e?.user && `${t('it.ip.user')}: ${e.user}`,
         e?.device_name && `${t('it.ip.device')}: ${e.device_name}`,
         e?.mac_address && `${t('it.ip.mac')}: ${e.mac_address}`]
        .filter(Boolean).join('\n') || `${ip}（${t('it.ip.free')}）`;

    let className = `aspect-square rounded-[3px] border flex items-center justify-center text-[10px] sm:text-[9px] font-mono leading-none transition-all duration-150 ${!reserved ? 'cursor-pointer hover:scale-110' : ''}`;
    let style;
    if (mode === 'purpose' && !reserved) {
      if (purpose) {
        const col = purposeColor(purpose);
        const isAssigned = st === 'assigned' || st === 'auth_free';
        style = isAssigned
          ? { backgroundColor: col, borderColor: col, color: contrastText(col) }
          : { backgroundColor: hexToRgba(col, 0.16), borderColor: hexToRgba(col, 0.35), color: '#64748b' };
      } else {
        className += ' bg-slate-50 border-slate-200 text-slate-300';
      }
    } else {
      className += ` ${STATE_CLS[st]}`;
    }

    cells.push(
      <button
        key={o}
        type="button"
        title={title}
        data-no-translate
        data-testid={`ip-cell-${o}`}
        disabled={reserved}
        style={style}
        onClick={() => !reserved && onCellClick(e || { ip_address: ip, octet: o, status: 'available' })}
        className={className}
      >
        {o}
      </button>,
    );
  }

  return (
    <div data-testid="ip-visual-map">
      {/* 8 columns on phones (finger-sized cells), 16 on larger screens */}
      <div className="grid gap-1 grid-cols-[repeat(8,minmax(0,1fr))] sm:grid-cols-[repeat(16,minmax(0,1fr))]">
        {cells}
      </div>
    </div>
  );
}

export function IpMapLegend({ mode = 'state' }) {
  if (mode === 'purpose') {
    return (
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-zinc-600" data-testid="ip-map-legend">
        {PURPOSES.map((p) => (
          <span key={p.label} className="inline-flex items-center gap-1.5">
            <span className="w-3.5 h-3.5 rounded-[3px] border" style={{ backgroundColor: p.color, borderColor: p.color }} />
            {p.label}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded-[3px] border bg-slate-50 border-slate-200" />未設定</span>
      </div>
    );
  }
  const items = [
    { cls: 'bg-slate-50 border-slate-200', label: '空き' },
    { cls: 'bg-blue-100 border-blue-300', label: '割当済み' },
    { cls: 'bg-emerald-100 border-emerald-300', label: '認証フリー' },
    { cls: 'bg-slate-200 border-slate-300', label: 'ネットワーク/ブロードキャスト' },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-zinc-600" data-testid="ip-map-legend">
      {items.map((i) => (
        <span key={i.label} className="inline-flex items-center gap-1.5">
          <span className={`w-3.5 h-3.5 rounded-[3px] border ${i.cls}`} />
          {i.label}
        </span>
      ))}
    </div>
  );
}
